import { useEffect, useRef, useState } from 'react';
import { Languages, Check, ChevronDown } from 'lucide-react';

// Languages exposed in the dropdown. Codes match Google Translate language codes.
const LANGS = [
  { code: 'en',    label: 'English',    flag: '🇬🇧' },
  { code: 'hi',    label: 'हिन्दी',     flag: '🇮🇳' },
  { code: 'bn',    label: 'বাংলা',      flag: '🇧🇩' },
  { code: 'ta',    label: 'தமிழ்',      flag: '🇮🇳' },
  { code: 'te',    label: 'తెలుగు',     flag: '🇮🇳' },
  { code: 'mr',    label: 'मराठी',      flag: '🇮🇳' },
  { code: 'gu',    label: 'ગુજરાતી',    flag: '🇮🇳' },
  { code: 'kn',    label: 'ಕನ್ನಡ',      flag: '🇮🇳' },
  { code: 'ml',    label: 'മലയാളം',    flag: '🇮🇳' },
  { code: 'pa',    label: 'ਪੰਜਾਬੀ',     flag: '🇮🇳' },
  { code: 'ur',    label: 'اردو',       flag: '🇵🇰' },
  { code: 'ar',    label: 'العربية',    flag: '🇸🇦' },
  { code: 'zh-CN', label: '中文',        flag: '🇨🇳' },
  { code: 'ja',    label: '日本語',      flag: '🇯🇵' },
  { code: 'ko',    label: '한국어',      flag: '🇰🇷' },
  { code: 'fr',    label: 'Français',   flag: '🇫🇷' },
  { code: 'de',    label: 'Deutsch',    flag: '🇩🇪' },
  { code: 'es',    label: 'Español',    flag: '🇪🇸' },
  { code: 'pt',    label: 'Português',  flag: '🇵🇹' },
  { code: 'it',    label: 'Italiano',   flag: '🇮🇹' },
  { code: 'ru',    label: 'Русский',    flag: '🇷🇺' },
  { code: 'th',    label: 'ไทย',         flag: '🇹🇭' },
  { code: 'vi',    label: 'Tiếng Việt', flag: '🇻🇳' },
];

const COOKIE_NAME = 'googtrans';

function readCurrent() {
  if (typeof document === 'undefined') return 'en';
  const c = document.cookie.split(';').map((s) => s.trim()).find((s) => s.startsWith(COOKIE_NAME + '='));
  if (!c) return 'en';
  const v = decodeURIComponent(c.split('=')[1] || '');
  // Cookie format is /en/<lang>
  const parts = v.split('/');
  return parts[2] || 'en';
}

function setCookie(name, value) {
  // Set cookie on the current host AND on the .domain root so Google reads it
  document.cookie = `${name}=${value}; path=/`;
  const host = window.location.hostname;
  const rootDomain = host.split('.').slice(-2).join('.');
  document.cookie = `${name}=${value}; domain=.${rootDomain}; path=/`;
  document.cookie = `${name}=${value}; domain=${host}; path=/`;
}

export default function LanguageSwitcher() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('en');
  const ref = useRef(null);

  useEffect(() => {
    setCurrent(readCurrent());
    // Inject hidden Google widget once
    if (!document.getElementById('hidden-gt-mount')) {
      const div = document.createElement('div');
      div.id = 'hidden-gt-mount';
      div.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:0;height:0;overflow:hidden;';
      document.body.appendChild(div);
    }
    if (!window.__gtInjected) {
      window.__gtInjected = true;
      window.googleTranslateElementInit = function () {
        try {
          // eslint-disable-next-line no-new
          new window.google.translate.TranslateElement(
            { pageLanguage: 'en', autoDisplay: false },
            'hidden-gt-mount',
          );
        } catch (e) { /* ignore */ }
      };
      const s = document.createElement('script');
      s.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  // Close on outside click
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, []);

  const pick = (code) => {
    setOpen(false);
    if (code === current) return;
    if (code === 'en') {
      // Clear cookies on all relevant domains
      const host = window.location.hostname;
      const rootDomain = host.split('.').slice(-2).join('.');
      ['', `; domain=.${rootDomain}`, `; domain=${host}`].forEach((d) => {
        document.cookie = `${COOKIE_NAME}=; path=/${d}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      });
    } else {
      setCookie(COOKIE_NAME, `/en/${code}`);
    }
    window.location.reload();
  };

  const active = LANGS.find((l) => l.code === current) || LANGS[0];

  return (
    <div ref={ref} className="lang-switcher notranslate" data-testid="lang-switcher">
      <button
        type="button"
        className="lang-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="lang-trigger-btn"
      >
        <span className="lang-icon"><Languages size={14} strokeWidth={2.4} /></span>
        <span className="lang-flag">{active.flag}</span>
        <span className="lang-label">{active.label}</span>
        <ChevronDown size={12} className={`lang-chev ${open ? 'open' : ''}`} />
      </button>
      {open && (
        <ul className="lang-menu" role="listbox">
          {LANGS.map((l) => (
            <li key={l.code}>
              <button
                type="button"
                onClick={() => pick(l.code)}
                className={`lang-option ${l.code === current ? 'active' : ''}`}
                role="option"
                aria-selected={l.code === current}
              >
                <span className="lang-flag">{l.flag}</span>
                <span className="lang-option-label">{l.label}</span>
                {l.code === current && <Check size={14} className="ml-auto text-[#f7941e]" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
